import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { PlugsConnected, CopySimple, ArrowsClockwise, Eye, EyeSlash, ArrowSquareOut } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

const ADMIN_EMAIL = "office@mw3d.at";

function SecretRow({ label, value, testid, onRegenerate, busy }) {
  const [show, setShow] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); toast.success(`${label} kopiert`); }
    catch { toast.error("Kopieren fehlgeschlagen"); }
  };
  const masked = value ? value.slice(0, 6) + "•".repeat(Math.max(6, value.length - 10)) + value.slice(-4) : "";
  return (
    <div className="border-2 border-gray-800 rounded-2xl p-3 bg-white">
      <p className="font-body text-xs text-gray-500 mb-1">{label}</p>
      {value ? (
        <div className="flex items-center gap-2">
          <code data-testid={`${testid}-value`} className="flex-1 font-mono text-xs text-gray-900 break-all bg-gray-50 rounded-lg px-2 py-1.5">{show ? value : masked}</code>
          <button data-testid={`${testid}-toggle`} onClick={() => setShow((s) => !s)} className="sb-btn p-2 bg-white shrink-0" title={show ? "Verbergen" : "Anzeigen"}>{show ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
          <button data-testid={`${testid}-copy`} onClick={copy} className="sb-btn p-2 bg-white shrink-0" title="Kopieren"><CopySimple size={16} weight="bold" /></button>
        </div>
      ) : (
        <p className="font-body text-sm text-gray-400">Noch nicht generiert</p>
      )}
      <button data-testid={`${testid}-regen`} disabled={busy} onClick={onRegenerate} className="sb-btn mt-2 px-3 py-1.5 bg-white text-xs flex items-center gap-1 disabled:opacity-50"><ArrowsClockwise size={14} weight="bold" />{value ? "Neu generieren" : "Generieren"}</button>
    </div>
  );
}

export function SupportIntegrationAdmin() {
  const { user } = useApp();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [outUrl, setOutUrl] = useState("");

  const isOffice = (user?.email || "").toLowerCase() === ADMIN_EMAIL;

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/support/integration"); setCfg(r.data); setOutUrl(r.data.outbound_url || ""); }
    catch { /* non-office admins won't see this component */ }
  }, []);
  useEffect(() => { if (isOffice) load(); }, [isOffice, load]);

  if (!isOffice) return null;

  const generate = async (target) => {
    setBusy(true);
    try {
      await api.post("/admin/support/integration/generate", { target });
      await load();
      toast.success("Neu generiert");
    } catch { toast.error("Fehler"); }
    finally { setBusy(false); }
  };

  const saveOutbound = async (patch) => {
    setBusy(true);
    try {
      const r = await api.put("/admin/support/integration", patch);
      setCfg((c) => ({ ...c, ...r.data }));
      toast.success("Gespeichert");
    } catch { toast.error("Fehler"); }
    finally { setBusy(false); }
  };

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(cfg.incoming_webhook_url); toast.success("Webhook-URL kopiert"); }
    catch { toast.error("Kopieren fehlgeschlagen"); }
  };

  return (
    <div className="sb-card p-5" data-testid="support-integration-admin">
      <h2 className="text-lg font-heading font-semibold mb-1 flex items-center gap-2"><PlugsConnected size={22} weight="duotone" /> Ticket-Schnittstelle · SafeBuddy Business</h2>
      <p className="font-body text-xs text-gray-500 mb-4">API-Key &amp; Webhook-Secret für die Ticket-Verbindung zu SafeBuddy Business. Nur für {ADMIN_EMAIL}.</p>

      {!cfg ? (
        <p className="font-body text-sm text-gray-500">…</p>
      ) : (
        <div className="space-y-3">
          <SecretRow label="API-Key (Business → Hauptapp, Header X-API-Key)" value={cfg.api_key} testid="integration-apikey" busy={busy} onRegenerate={() => generate("api_key")} />
          <SecretRow label="Webhook-Secret (Header X-Webhook-Secret)" value={cfg.webhook_secret} testid="integration-secret" busy={busy} onRegenerate={() => generate("webhook_secret")} />

          <button data-testid="integration-generate-both" disabled={busy} onClick={() => generate("both")} className="sb-btn w-full py-2 text-white text-sm font-medium disabled:opacity-50" style={{ background: "var(--app-primary)" }}>Beide neu generieren</button>

          <div className="border-2 border-gray-800 rounded-2xl p-3 bg-white">
            <p className="font-body text-xs text-gray-500 mb-1">Eingehender Webhook (Business sendet Ticket-Updates hierher)</p>
            <div className="flex items-center gap-2">
              <code data-testid="integration-incoming-url" className="flex-1 font-mono text-xs text-gray-900 break-all bg-gray-50 rounded-lg px-2 py-1.5">{cfg.incoming_webhook_url}</code>
              <button data-testid="integration-incoming-copy" onClick={copyUrl} className="sb-btn p-2 bg-white shrink-0" title="Kopieren"><CopySimple size={16} weight="bold" /></button>
            </div>
            <p className="font-body text-[11px] text-gray-400 mt-1">POST · Header <code>X-Webhook-Secret</code> · validiert gegen das Secret oben.</p>
          </div>

          <div className="border-2 border-gray-800 rounded-2xl p-3 bg-white space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-body font-medium text-sm">Ausgehende Weiterleitung</p>
                <p className="font-body text-xs text-gray-500 mt-0.5">Neue Hauptapp-Tickets automatisch an Business senden</p>
              </div>
              <Switch data-testid="integration-outbound-toggle" checked={!!cfg.outbound_enabled} onCheckedChange={(v) => { setCfg((c) => ({ ...c, outbound_enabled: v })); saveOutbound({ outbound_enabled: v }); }} />
            </div>
            <label className="font-body text-xs text-gray-500">Ziel-URL (Business Webhook)
              <div className="flex items-center gap-2 mt-1">
                <input data-testid="integration-outbound-url" className="sb-input" value={outUrl} onChange={(e) => setOutUrl(e.target.value)} placeholder="https://business.safe-buddy.com/api/support/webhook" />
                <button data-testid="integration-outbound-save" disabled={busy} onClick={() => saveOutbound({ outbound_url: outUrl })} className="sb-btn px-3 bg-white text-sm shrink-0 disabled:opacity-50">Speichern</button>
              </div>
            </label>
            <a href="https://business.safe-buddy.com" target="_blank" rel="noreferrer" className="font-body text-[11px] text-gray-400 inline-flex items-center gap-1">business.safe-buddy.com <ArrowSquareOut size={11} /></a>
          </div>
        </div>
      )}
    </div>
  );
}
